import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { appConfig } from "../../../../app.config";
import { onListeAttente, type Product } from "./actions";

/**
 * Les trois prix et la fenetre de la fausse porte (spec, sections 1 et 7). Le clic sur un
 * prix ne simule aucun achat : la fenetre dit tout de suite que rien n'est vendu
 * aujourd'hui et propose seulement de laisser une adresse, avec un consentement explicite
 * (case non cochee) et un champ piege pour les robots.
 */
export interface Offer {
  id: Product;
  name: string;
  price: string;
  detail: string;
}

export const OFFERS: readonly Offer[] = [
  {
    id: "fichier-hd",
    name: "Fichier HD",
    price: "7,90 €",
    detail: "Ton affiche en haute définition, prête à imprimer.",
  },
  {
    id: "annuel",
    name: "Premium",
    price: "12 € par an",
    detail: "Tous les thèmes, des affiches HD sans limite, ta liste sauvegardée.",
  },
  {
    id: "a-vie",
    name: "Premium à vie",
    price: "29 €",
    detail: "Le Premium, payé une seule fois.",
  },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

export interface FalseDoorProps {
  offer: Offer;
  onClose: () => void;
}

export function FalseDoor({ offer, onClose }: FalseDoorProps) {
  const titleId = useId();
  const emailRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [trap, setTrap] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  // Fenetre modale : le reste de la page devient inerte (ni focus ni clic), son defilement
  // est bloque, et le focus revient au bouton de prix a la fermeture.
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const wrapper = wrapperRef.current;
    const others = Array.from(document.body.children).filter(
      (element) => element !== wrapper && !element.hasAttribute("inert"),
    );
    for (const element of others) {
      element.setAttribute("inert", "");
    }
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    emailRef.current?.focus();
    return () => {
      for (const element of others) {
        element.removeAttribute("inert");
      }
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);

  useEffect(() => {
    if (done) {
      doneRef.current?.focus();
    }
  }, [done]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const address = email.trim();
    if (!EMAIL_PATTERN.test(address)) {
      setError("Cette adresse ne semble pas valide.");
      return;
    }
    if (!consent) {
      setError("Coche la case pour qu'on puisse t'écrire.");
      return;
    }
    setError("");
    setSending(true);
    try {
      const result = await onListeAttente({
        email: address,
        product: offer.id,
        consent,
        piege: trap,
      });
      if (result.ok) {
        setDone(true);
      } else {
        setError(result.erreur ?? "L'inscription n'a pas marché. Réessaie plus tard.");
      }
    } catch {
      setError("L'inscription n'a pas marché. Réessaie plus tard.");
    } finally {
      setSending(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
      ref={wrapperRef}
    >
      <button
        aria-label="Fermer la fenêtre"
        className="absolute inset-0 cursor-default bg-black/60"
        onClick={onClose}
        tabIndex={-1}
        type="button"
      />
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className="relative w-full max-w-md rounded-lg bg-card p-6 text-card-foreground shadow-xl"
        role="dialog"
      >
        <h2 className="text-xl font-semibold" id={titleId}>
          Ce n'est pas encore prêt.
        </h2>
        {done ? (
          <>
            <p className="mt-3">C'est noté. On t'écrit à l'ouverture, une seule fois.</p>
            <button
              className="mt-6 h-11 w-full rounded-md bg-primary px-4 font-medium text-primary-foreground"
              onClick={onClose}
              ref={doneRef}
              type="button"
            >
              Fermer
            </button>
          </>
        ) : (
          <form className="mt-3 flex flex-col gap-4" noValidate onSubmit={submit}>
            <p>Laisse ton e-mail, on te prévient à l'ouverture.</p>
            <p className="text-sm text-muted-foreground">
              Tu as choisi {offer.name} ({offer.price}). Rien n'est vendu aujourd'hui : aucun
              paiement ne t'est demandé.
            </p>
            <label className="flex flex-col gap-1 text-sm font-medium">
              Ton e-mail
              <input
                autoComplete="email"
                className="h-11 rounded-md border border-input bg-background px-3 text-base font-normal"
                inputMode="email"
                name="email"
                onChange={(event) => setEmail(event.target.value)}
                ref={emailRef}
                type="email"
                value={email}
              />
            </label>
            <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
              <label>
                Ne pas remplir ce champ
                <input
                  autoComplete="off"
                  name="site"
                  onChange={(event) => setTrap(event.target.value)}
                  tabIndex={-1}
                  type="text"
                  value={trap}
                />
              </label>
            </div>
            <label className="flex items-start gap-3 text-sm">
              <input
                checked={consent}
                className="mt-1 h-4 w-4 shrink-0"
                name="consent"
                onChange={(event) => setConsent(event.target.checked)}
                type="checkbox"
              />
              <span>
                J'accepte qu'on m'écrive une seule fois, pour me prévenir de l'ouverture. Je peux
                retirer mon accord à tout moment en écrivant à{" "}
                <a className="underline" href={`mailto:${appConfig.contactEmail}`}>
                  {appConfig.contactEmail}
                </a>
                .
              </span>
            </label>
            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                className="h-11 rounded-md border border-input px-4 font-medium"
                onClick={onClose}
                type="button"
              >
                Fermer
              </button>
              <button
                className="h-11 rounded-md bg-primary px-4 font-medium text-primary-foreground disabled:opacity-60"
                disabled={sending}
                type="submit"
              >
                Me prévenir
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
